import { animated } from '@react-spring/web';
import React, { FunctionComponent } from 'react';
import {
  Direction,
  SlideInWithBackdrop
} from '../../animation/SlideInWithBackdrop/SlideInWithBackdrop';
import { FixedContentPortal } from '../../fixedContent/FixedContentPortal/FixedContentPortal';
import { Icon, IconType } from '../../misc/Icon/Icon';
import { prepareProps } from '../../utils/propUtils';
import { classes } from '../../utils/utils';
import styles from './SidebarWithBackdrop.module.css';

export enum Side {
  Left = 'left',
  Right = 'right'
}

export type SidebarWithBackdropProps = {
  opened: boolean;
  /**
   * default: left
   */
  side?: Side;
  /**
   * this is called when something occured which wants to close the sidebar
   */
  onCloseSidebar: () => void;

  'data-testid'?: string;
};

/**
 * the content will be placed in a flex layout
 */
export const SidebarWithBackdrop: FunctionComponent<SidebarWithBackdropProps> =
  React.memo((props) => {
    const {
      children,
      opened,
      side = Side.Left,
      'data-testid': testid,
      onCloseSidebar
    } = prepareProps({ props });

    return (
      <div
        className={styles.placeholder}
        data-testid={testid}
      >
        <SlideInWithBackdrop
          show={opened}
          direction={sideToAnimationDirection[side]}
        >
          {(slideStyle, backdropStyle) => (
            <FixedContentPortal>
              <animated.div
                className={styles.backdrop}
                style={backdropStyle}
                onClick={onCloseSidebar}
                data-testid="SidebarWithBackdrop.backdrop"
              ></animated.div>
              <animated.aside
                className={classes(styles.aside, sideToAsideClassName[side])}
                style={slideStyle}
                data-testid={`${testid ? `${testid}.` : ''}SidebarWithBackdrop.ContentWrapper`}
              >
                <div className={styles.header}>
                  <Icon
                    name="times"
                    type={IconType.Light}
                    className={styles.headerIcon}
                    onClick={onCloseSidebar}
                  ></Icon>
                  <span className={styles.headerCompanyName}>recordIT</span>
                </div>
                {children}
              </animated.aside>
            </FixedContentPortal>
          )}
        </SlideInWithBackdrop>
      </div>
    );
  });

const sideToAsideClassName: Record<Side, string> = {
  left: styles.asideLeft,
  right: styles.asideRight
};

const sideToAnimationDirection: Record<Side, Direction> = {
  left: Direction.LeftToRight,
  right: Direction.RightToLeft
};
